<script lang="ts">
  import { _ } from 'svelte-i18n';
  import { capital } from '$lib/stores/selection';

  let useCapitalFilter = false;

  const minCapital = 0;
  const maxCapital = 10_000_000;
  const step = 10_000;

  let capitalMin = minCapital;
  let sliderElement: HTMLElement | undefined;
  let isDragging = false;

  // Handle checkbox toggle
  function toggleCapitalFilter() {
    useCapitalFilter = !useCapitalFilter;
    if (!useCapitalFilter) {
      capital.set(null);
      capitalMin = minCapital;
    } else {
      capital.set(capitalMin);
    }
  }

  // Sync checkbox when filter is changed externally (from filter pills or AI)
  $: if ($capital === null) {
    useCapitalFilter = false;
    capitalMin = minCapital;
  } else if ($capital !== null) {
    useCapitalFilter = true;
    capitalMin = $capital;
  }

  // Handle slider drag events
  function handleMouseMove(e: MouseEvent) {
    if (!isDragging || !sliderElement) return;

    const rect = sliderElement.getBoundingClientRect();
    const percent = Math.max(0, Math.min(100, ((e.clientX - rect.left) / rect.width) * 100));
    const value = Math.round(((percent / 100) * maxCapital) / step) * step;
    capitalMin = value;
  }

  function handleMouseUp() {
    isDragging = false;
  }

  // Update store when slider value changes (only if filter is active)
  $: if (useCapitalFilter && capitalMin !== null) {
    capital.set(capitalMin);
  }
</script>

<svelte:window on:mousemove={handleMouseMove} on:mouseup={handleMouseUp} />

<!-- Capital Filter -->
<div class={useCapitalFilter ? 'm-2 rounded-control bg-surface-well p-3' : 'p-3'}>
  <!-- Toggle -->
  <div class="mb-3">
    <label class="group flex cursor-pointer items-center">
      <input
        type="checkbox"
        checked={useCapitalFilter}
        on:click={toggleCapitalFilter}
        class="mr-2 h-3 w-3 rounded border-gray-300 text-accent-600 focus:ring-1 focus:ring-accent-200"
      />
      <svg class="filter-mark mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="2"
          d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1"
        />
      </svg>
      <span class="text-xs font-semibold text-gray-900 transition-colors group-hover:text-gray-700">
        {$_('filters.capital.minimum')}
      </span>
    </label>
  </div>

  {#if useCapitalFilter}
    <div class="space-y-4">
      <!-- Custom Slider -->
      <div class="relative mb-6">
        <!-- Slider track -->
        <div class="relative h-1 rounded-full bg-gray-200" bind:this={sliderElement}>
          <!-- Active range from start -->
          <div
            class="absolute h-full rounded-full bg-accent-500"
            style="left: 0; width: {(capitalMin / maxCapital) * 100}%"
          ></div>

          <!-- Single handle -->
          <div
            class="absolute top-1/2 -translate-y-1/2 cursor-grab"
            style="left: {(capitalMin / maxCapital) * 100}%"
            on:mousedown={(e) => {
              e.preventDefault();
              isDragging = true;
            }}
            role="slider"
            tabindex="0"
            aria-label={$_('a11y.minimumCapital')}
            aria-valuemin={minCapital}
            aria-valuemax={maxCapital}
            aria-valuenow={capitalMin}
          >
            <div
              class="h-4 w-4 -translate-x-1/2 rounded-full bg-white shadow-raised ring-2 ring-accent-500 transition-colors"
            ></div>
            <div
              class="pointer-events-none absolute -top-6 left-1/2 -translate-x-1/2 rounded bg-gray-800 px-2 py-0.5 text-xs text-white opacity-0 transition-opacity {isDragging
                ? 'opacity-100'
                : ''}"
            >
              CHF {capitalMin.toLocaleString('de-CH')}
            </div>
          </div>
        </div>

        <!-- Reference points -->
        <div class="mt-2 flex justify-between text-2xs text-gray-500">
          <span>0</span>
          <span>2.5M</span>
          <span>5M</span>
          <span>7.5M</span>
          <span>10M</span>
        </div>
      </div>

      <!-- Numeric Input -->
      <div>
        <label class="mb-1 block text-xs text-gray-500">{$_('filters.capital.minimum')}</label>
        <input
          type="number"
          bind:value={capitalMin}
          min={minCapital}
          max={maxCapital}
          {step}
          class="w-full rounded-control border border-gray-300 px-2 py-1 text-center text-xs focus:border-accent-500 focus:outline-none focus:ring-1 focus:ring-accent-500"
        />
      </div>
    </div>
  {/if}
</div>
